import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, Search, ThumbsUp, ThumbsDown, Copy, ExternalLink, X, Plus } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const SOURCE_TYPE_LABELS = {
  gmail_thread: 'Gmail',
  helpdesk_ticket: 'Ticket',
  freshdesk_legacy: 'Freshdesk',
  manual: 'Manual',
};

const SIMILARITY_TIERS = [
  { min: 0.50, key: 'high', color: '#22c55e' },
  { min: 0.35, key: 'medium', color: '#f59e0b' },
  { min: 0, key: 'low', color: '#ef4444' },
];

function getSimilarityTier(sim) {
  return SIMILARITY_TIERS.find(t => sim >= t.min) || SIMILARITY_TIERS[2];
}

function stripHtml(s) {
  if (!s) return '';
  return String(s).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

export default function AiAssistPanel({ open, onClose, contextText, clientId, onInsert }) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState({});
  const lastQuery = useRef('');
  const autoFiredFor = useRef(null);

  const runSearch = useCallback(async (q) => {
    const term = (q || '').trim();
    if (!term) return;
    setLoading(true);
    setSearched(true);
    lastQuery.current = term;
    try {
      const res = await api.post('/rag/search', {
        query: term,
        clientId: clientId || undefined,
        limit: 10,
      });
      setResults(res.data.results || []);
      setFeedbackGiven({});
    } catch {
      toast.error(t('aiAssist.searchFailed'));
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, t]);

  // On open: seed query from context and auto-fire (once per open+context combo)
  useEffect(() => {
    if (!open) {
      autoFiredFor.current = null;
      return;
    }
    const seed = stripHtml(contextText || '').slice(0, 500);
    const fingerprint = `${seed}::${clientId || ''}`;
    if (autoFiredFor.current === fingerprint) return;
    autoFiredFor.current = fingerprint;
    setQuery(seed);
    setResults([]);
    setSearched(false);
    if (seed) runSearch(seed);
  }, [open, contextText, clientId, runSearch]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleFeedback = async (chunkId, rating) => {
    try {
      await api.post('/rag/feedback', {
        chunk_id: chunkId,
        query_text: lastQuery.current,
        rating,
      });
      setFeedbackGiven(prev => ({ ...prev, [chunkId]: rating }));
      toast.success(rating === 1 ? t('aiAssist.thanksUp') : t('aiAssist.thanksDown'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t('common.copied'));
  };

  const handleInsert = (text) => {
    if (typeof onInsert === 'function') {
      onInsert(text);
      toast.success(t('aiAssist.inserted'));
    }
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        onMouseDown={(e) => { if (e.target === e.currentTarget) { (onClose)(e); } }}
        style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)',
          zIndex: 9998,
        }}
      />
      {/* Slide-over panel */}
      <div
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0,
          width: 'min(480px, 100vw)', background: 'var(--c-bg)',
          borderLeft: '1px solid var(--c-border)', zIndex: 9999,
          display: 'flex', flexDirection: 'column',
          boxShadow: '-8px 0 24px rgba(0,0,0,0.15)',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '14px 16px', borderBottom: '1px solid var(--c-border)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <Sparkles size={18} style={{ color: '#8b5cf6' }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{t('aiAssist.title')}</div>
            <div style={{ fontSize: 11, color: 'var(--c-text-muted)' }}>{t('aiAssist.subtitle')}</div>
          </div>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-icon btn-sm"
            title={t('common.close')}
          >
            <X size={16} />
          </button>
        </div>

        {/* Search box */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--c-border-light, rgba(0,0,0,0.06))' }}>
          <textarea
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); runSearch(query); } }}
            placeholder={t('aiAssist.placeholder')}
            rows={3}
            style={{
              width: '100%', padding: '8px 10px', borderRadius: 6,
              border: '1px solid var(--c-border)', resize: 'vertical',
              fontSize: 13, fontFamily: 'inherit',
              background: 'var(--c-surface)', color: 'var(--c-text)',
            }}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
            <button
              type="button"
              onClick={() => runSearch(query)}
              disabled={loading || !query.trim()}
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: 12 }}
            >
              <Search size={13} />
              {loading ? t('aiAssist.searching') : t('aiAssist.search')}
            </button>
          </div>
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: 30, color: 'var(--c-text-muted)', fontSize: 13 }}>
              {t('aiAssist.searching')}…
            </div>
          )}

          {!loading && searched && results.length === 0 && (
            <div style={{ textAlign: 'center', padding: 30, color: 'var(--c-text-muted)', fontSize: 13 }}>
              {t('aiAssist.noResults')}
            </div>
          )}

          {!loading && results.length > 0 && (
            <>
              <div style={{ fontSize: 11, color: 'var(--c-text-muted)', marginBottom: 8, padding: '0 4px' }}>
                {results.length} {t('aiAssist.resultsFound')}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {results.map((r) => {
                  const tier = getSimilarityTier(r.similarity);
                  const fb = feedbackGiven[r.chunk_id];
                  return (
                    <div key={r.chunk_id} style={{
                      border: '1px solid var(--c-border)', borderRadius: 8,
                      padding: 12, background: 'var(--c-surface)',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{
                          padding: '1px 6px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                          background: tier.color + '20', color: tier.color, border: `1px solid ${tier.color}40`,
                        }}>
                          {(r.similarity * 100).toFixed(0)}% {t(`aiAssist.tier.${tier.key}`)}
                        </span>
                        <span style={{
                          padding: '1px 5px', borderRadius: 4, fontSize: 10,
                          background: 'var(--c-bg)', border: '1px solid var(--c-border)', color: 'var(--c-text-muted)',
                        }}>
                          {SOURCE_TYPE_LABELS[r.source_type] || r.source_type}
                        </span>
                        {r.source_date && (
                          <span style={{ fontSize: 10, color: 'var(--c-text-muted)' }}>
                            {new Date(r.source_date).toLocaleDateString('es-AR')}
                          </span>
                        )}
                      </div>
                      {r.title && (
                        <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 4, color: 'var(--c-text)' }}>
                          {r.title}
                        </div>
                      )}
                      <div style={{
                        fontSize: 12, lineHeight: 1.5, whiteSpace: 'pre-wrap',
                        color: 'var(--c-text)', marginBottom: 8,
                      }}>
                        {r.summary}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {typeof onInsert === 'function' && (
                          <button
                            onClick={() => handleInsert(r.summary)}
                            className="btn btn-ghost"
                            style={{ padding: '3px 8px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, color: 'var(--c-primary)' }}
                            title={t('aiAssist.insert')}
                          >
                            <Plus size={11} /> {t('aiAssist.insert')}
                          </button>
                        )}
                        <button
                          onClick={() => handleCopy(r.summary)}
                          className="btn btn-ghost"
                          style={{ padding: '3px 8px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}
                          title={t('common.copy')}
                        >
                          <Copy size={11} />
                        </button>
                        {r.source_type === 'helpdesk_ticket' && r.external_id && (
                          <a
                            href={`/tickets/${r.external_id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-ghost"
                            style={{ padding: '3px 8px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, textDecoration: 'none' }}
                            title={t('aiAssist.viewOriginal')}
                          >
                            <ExternalLink size={11} />
                          </a>
                        )}
                        <div style={{ flex: 1 }} />
                        <button
                          onClick={() => handleFeedback(r.chunk_id, 1)}
                          disabled={fb !== undefined}
                          className="btn btn-ghost"
                          style={{
                            padding: '3px 8px', fontSize: 11,
                            color: fb === 1 ? '#22c55e' : undefined,
                            opacity: fb !== undefined && fb !== 1 ? 0.3 : 1,
                          }}
                          title={t('aiAssist.helpful')}
                        >
                          <ThumbsUp size={12} />
                        </button>
                        <button
                          onClick={() => handleFeedback(r.chunk_id, -1)}
                          disabled={fb !== undefined}
                          className="btn btn-ghost"
                          style={{
                            padding: '3px 8px', fontSize: 11,
                            color: fb === -1 ? '#ef4444' : undefined,
                            opacity: fb !== undefined && fb !== -1 ? 0.3 : 1,
                          }}
                          title={t('aiAssist.notHelpful')}
                        >
                          <ThumbsDown size={12} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {!loading && !searched && (
            <div style={{ textAlign: 'center', padding: 30, color: 'var(--c-text-muted)', fontSize: 12 }}>
              {t('aiAssist.emptyHint')}
            </div>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}
